import {
  ACCT_UNIT_OPTIONS as DEFAULT_ACCT_UNIT_OPTIONS,
  acctUnitLabel as masterAcctUnitLabel,
} from 'budget/budgetAcctStore';
import { isBdApiEnabled } from 'config/bdApiConfig';
import { commonSelect, commonSaveList, cacheGet, cacheSet } from 'services/commonQuery';
import { loadCodeOptions } from 'master/commonCodeStore';

/**
 * 기준정보 — 부서관리 (PoC · localStorage / HrOrgUnit API)
 *
 * - 단일 부서 테이블: 상위부서 + 예산역할(편성/집행/편성+집행)
 * - 조직개편·속성변경 이력은 deptUid 단위로 별도 저장
 * - API: orgUnitId ↔ deptUid (String)
 * - 조직레벨: 공통코드 그룹 `orglevel` (code=1~4 → hr_org_unit.org_level TINYINT)
 */

export const DEPT_STORAGE_PREFIX = 'erp.master.dept';
export const DEPT_HISTORY_PREFIX = 'erp.master.deptHistory';

/** 공통코드 그룹ID — 조직등록 조직레벨 콤보 */
export const ORG_LEVEL_CODE_GROUP = 'orglevel';

const deptCache = new Map();
/** @type {Array<{value:string,label:string}>} */
let orgLevelOptionsCache = null;

/**
 * 조직 레벨 코드 (공통코드 orglevel 과 동일)
 * 1=본부 2=부 3=과 4=팀
 */
export const ORG_LEVEL = {
  HQ: '1',
  DEPT: '2',
  SECTION: '3',
  TEAM: '4',
};

/** API 실패·미로드 시 폴백 (등록된 orglevel 과 동일 값) */
export const ORG_LEVEL_OPTIONS = [
  { value: '1', label: '본부' },
  { value: '2', label: '부' },
  { value: '3', label: '과' },
  { value: '4', label: '팀' },
];

/** 공통코드 orglevel → 콤보 옵션 (캐시) */
export async function loadOrgLevelOptions() {
  if (!isBdApiEnabled()) {
    orgLevelOptionsCache = ORG_LEVEL_OPTIONS;
    return ORG_LEVEL_OPTIONS;
  }
  try {
    const opts = await loadCodeOptions(ORG_LEVEL_CODE_GROUP);
    orgLevelOptionsCache = opts.length ? opts : ORG_LEVEL_OPTIONS;
  } catch {
    orgLevelOptionsCache = ORG_LEVEL_OPTIONS;
  }
  return orgLevelOptionsCache;
}

export function getOrgLevelOptions() {
  return orgLevelOptionsCache && orgLevelOptionsCache.length
    ? orgLevelOptionsCache
    : ORG_LEVEL_OPTIONS;
}

/** 예산 역할 — 편성부서/집행부서 분리 테이블 대신 역할로 구분 */
export const BUDGET_ROLE = {
  BUDGET: 'BUDGET',
  EXEC: 'EXEC',
  BOTH: 'BOTH',
};

export const BUDGET_ROLE_OPTIONS = [
  { value: 'BUDGET', label: '편성' },
  { value: 'EXEC', label: '집행' },
  { value: 'BOTH', label: '편성+집행' },
];

/** 기존 import 호환용 기본 옵션 — 화면에서는 기관별 acctUnitOptions를 사용한다. */
export const ACCT_UNIT_OPTIONS = DEFAULT_ACCT_UNIT_OPTIONS;

export const YN_OPTIONS = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
];

/** 이력 변경유형 */
export const DEPT_CHANGE_TYPE = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  MOVE: 'MOVE',
  ROLE: 'ROLE',
  CLOSE: 'CLOSE',
  REORG: 'REORG',
};

export const DEPT_CHANGE_TYPE_LABEL = {
  CREATE: '신규',
  UPDATE: '수정',
  MOVE: '상위변경',
  ROLE: '역할변경',
  CLOSE: '폐지',
  REORG: '조직개편',
};

export function createDeptUid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `DU-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function deptStorageKey(tenantCode) {
  return `${DEPT_STORAGE_PREFIX}|${tenantCode || ''}`;
}

export function deptHistoryKey(tenantCode, deptUid) {
  return `${DEPT_HISTORY_PREFIX}|${tenantCode || ''}|${deptUid || ''}`;
}

export function budgetRoleLabel(code) {
  return BUDGET_ROLE_OPTIONS.find((o) => o.value === code)?.label || code || '';
}

export function acctUnitLabel(code, tenantCode) {
  return masterAcctUnitLabel(code, tenantCode);
}

export function orgLevelLabel(code) {
  const key = code != null && code !== '' ? String(code) : '';
  return getOrgLevelOptions().find((o) => o.value === key)?.label || key || '';
}

export function changeTypeLabel(code) {
  return DEPT_CHANGE_TYPE_LABEL[code] || code || '';
}

/** 상위 코드 체인 → 경로코드 (예: HQ-A-A01) */
export function buildDeptPathCode(row, allRows = []) {
  if (!row?.deptCode) return '';
  const byCode = new Map((allRows || []).map((r) => [String(r.deptCode), r]));
  const parts = [];
  let cur = row;
  let guard = 0;
  while (cur && guard++ < 24) {
    parts.unshift(String(cur.deptCode));
    if (!cur.parentDeptCode) break;
    cur = byCode.get(String(cur.parentDeptCode)) || null;
  }
  return parts.join('-');
}

export function enrichDeptRows(rows = [], tenantCode) {
  const list = (rows || []).map((r) => ({
    ...r,
    deptUid: r.deptUid || createDeptUid(),
  }));
  return list.map((r) => ({
    ...r,
    pathCode: buildDeptPathCode(r, list),
    budgetRoleNm: budgetRoleLabel(r.budgetRole),
    orgLevelNm: orgLevelLabel(r.orgLevel),
    acctUnitNm: acctUnitLabel(r.acctUnit, tenantCode),
  }));
}

function loadRaw(tenantCode) {
  try {
    const raw = localStorage.getItem(deptStorageKey(tenantCode));
    if (!raw) return null;
    const data = JSON.parse(raw);
    return Array.isArray(data?.rows) ? data.rows : null;
  } catch {
    return null;
  }
}

/** PoC 샘플: A부(편성) + a·b·c팀(집행) */
export function buildSampleDepts() {
  const hqUid = createDeptUid();
  const aUid = createDeptUid();
  const a1 = createDeptUid();
  const a2 = createDeptUid();
  const a3 = createDeptUid();
  return enrichDeptRows([
    {
      deptUid: hqUid,
      deptCode: '100',
      deptName: '본사',
      parentDeptCode: '',
      orgLevel: ORG_LEVEL.HQ,
      budgetRole: BUDGET_ROLE.BUDGET,
      useYn: 'Y',
      sortOrder: 10,
      remark: '본부',
      mod: '',
    },
    {
      deptUid: aUid,
      deptCode: 'A',
      deptName: 'A부',
      parentDeptCode: '100',
      orgLevel: ORG_LEVEL.DEPT,
      budgetRole: BUDGET_ROLE.BUDGET,
      useYn: 'Y',
      sortOrder: 20,
      remark: '예산 편성 부서',
      mod: '',
    },
    {
      deptUid: a1,
      deptCode: 'A01',
      deptName: 'a팀',
      parentDeptCode: 'A',
      orgLevel: ORG_LEVEL.TEAM,
      budgetRole: BUDGET_ROLE.EXEC,
      useYn: 'Y',
      sortOrder: 30,
      remark: '',
      mod: '',
    },
    {
      deptUid: a2,
      deptCode: 'A02',
      deptName: 'b팀',
      parentDeptCode: 'A',
      orgLevel: ORG_LEVEL.TEAM,
      budgetRole: BUDGET_ROLE.EXEC,
      useYn: 'Y',
      sortOrder: 40,
      remark: '',
      mod: '',
    },
    {
      deptUid: a3,
      deptCode: 'A03',
      deptName: 'c팀',
      parentDeptCode: 'A',
      orgLevel: ORG_LEVEL.TEAM,
      budgetRole: BUDGET_ROLE.EXEC,
      useYn: 'Y',
      sortOrder: 50,
      remark: '',
      mod: '',
    },
    {
      deptUid: createDeptUid(),
      deptCode: 'B',
      deptName: '총무과',
      parentDeptCode: '100',
      orgLevel: ORG_LEVEL.SECTION,
      budgetRole: BUDGET_ROLE.BOTH,
      useYn: 'Y',
      sortOrder: 60,
      remark: '편성+집행 겸용 예시',
      mod: '',
    },
  ]);
}

/** DB org_unit_id(BIGINT) 여부 — FE UUID(deptUid) 는 false */
function isDbOrgUnitId(id) {
  return id != null && id !== '' && /^\d+$/.test(String(id));
}

/** API 행 → FE 부서 행 */
function mapOrgUnitToDept(row, byOrgId) {
  const dbId = isDbOrgUnitId(row.orgUnitId) ? String(row.orgUnitId) : '';
  const deptUid = dbId || String(row.deptUid || createDeptUid());
  const parentId = row.parentOrgUnitId != null ? String(row.parentOrgUnitId) : '';
  const parent = parentId ? byOrgId.get(parentId) : null;
  const status = String(row.status || '').toUpperCase();
  const useYn =
    row.useYn === 'Y' || row.useYn === 'N'
      ? row.useYn
      : status === 'ABOLISHED' || status === 'INACTIVE'
        ? 'N'
        : 'Y';
  return {
    ...row,
    deptUid,
    orgUnitId: dbId || undefined,
    deptCode: row.deptCode || row.orgUnitCode || '',
    deptName: row.deptName || row.orgUnitNm || '',
    parentDeptCode: parent?.deptCode || parent?.orgUnitCode || row.parentDeptCode || '',
    orgLevel: row.orgLevel != null && row.orgLevel !== '' ? String(row.orgLevel) : ORG_LEVEL.DEPT,
    budgetRole: row.budgetRole || BUDGET_ROLE.BOTH,
    useYn,
    sortOrder: row.sortOrder ?? 10,
    remark: row.remark || '',
    mod: '',
  };
}

/**
 * 저장 행 — parent 는 parentDeptCode 로 Mapper 가 BIGINT 조회.
 * orgUnitId 는 DB 숫자만 (신규 A 는 null → AUTO_INCREMENT).
 */
function toOrgUnitSaveRow(row) {
  const parentCode = String(row.parentDeptCode || '').trim();
  const orgUnitId = isDbOrgUnitId(row.orgUnitId) ? Number(row.orgUnitId) : null;
  return {
    deptCode: row.deptCode,
    deptName: row.deptName,
    parentDeptCode: parentCode || null,
    // 공통코드 orglevel code(1~4) → DB TINYINT org_level
    orgLevel: (() => {
      const n = Number(row.orgLevel);
      return Number.isFinite(n) && n > 0 ? n : Number(ORG_LEVEL.DEPT);
    })(),
    orgPath: row.pathCode ? `/${String(row.pathCode).replace(/-/g, '/')}/` : undefined,
    sortOrder: row.sortOrder,
    status: row.mod === 'D' || row.useYn === 'N' ? 'ABOLISHED' : 'ACTIVE',
    budgetRole: row.budgetRole,
    orgUnitId,
    mod: row.mod,
  };
}

/** 동일 배치: 삭제(자식→부모) 후 추가/수정(부모→자식) */
function sortDeptSaveRows(rows) {
  const list = Array.isArray(rows) ? [...rows] : [];
  const codeOf = (r) => String(r?.deptCode || '').trim();
  const parentOf = (r) => String(r?.parentDeptCode || '').trim();
  const byCode = new Map();
  list.forEach((r) => {
    const c = codeOf(r);
    if (c) byCode.set(c, r);
  });
  const depth = (r, guard = 0) => {
    const p = parentOf(r);
    if (!p || !byCode.has(p) || guard > 24) return 0;
    return 1 + depth(byCode.get(p), guard + 1);
  };
  const deletes = list.filter((r) => r.mod === 'D').sort((a, b) => depth(b) - depth(a));
  const upserts = list
    .filter((r) => r.mod === 'A' || r.mod === 'U')
    .sort((a, b) => depth(a) - depth(b));
  return [...deletes, ...upserts];
}

export async function loadDepts(tenantCode) {
  const tc = String(tenantCode || '').trim();
  if (isBdApiEnabled()) {
    const rows = await commonSelect('HrOrgUnit.selectOrgUnitList', { tenantCode: tc });
    const byOrgId = new Map((rows || []).map((r) => [String(r.orgUnitId), r]));
    const mapped = enrichDeptRows(
      (rows || []).map((r) => mapOrgUnitToDept(r, byOrgId)),
      tc
    );
    cacheSet(deptCache, tc, mapped);
    return mapped;
  }
  const existing = loadRaw(tenantCode);
  if (existing && existing.length) {
    const mapped = enrichDeptRows(existing, tenantCode);
    cacheSet(deptCache, tc, mapped);
    return mapped;
  }
  const seeded = buildSampleDepts();
  await saveDepts(tenantCode, seeded);
  seeded.forEach((r) => {
    appendDeptHistory(tenantCode, r.deptUid, {
      changeType: DEPT_CHANGE_TYPE.CREATE,
      changeAt: new Date().toISOString(),
      changeBy: 'system',
      memo: '초기 샘플 등록',
      snapshot: snapshotDept(r),
    });
  });
  return seeded;
}

/** 동기 lookup용 — loadDepts 이후 캐시 */
export function getCachedDepts(tenantCode) {
  const tc = String(tenantCode || '').trim();
  return cacheGet(deptCache, tc) || [];
}

export async function saveDepts(tenantCode, rows) {
  const tc = String(tenantCode || '').trim();
  const enriched = enrichDeptRows(rows || [], tenantCode);

  if (isBdApiEnabled()) {
    const dirty = sortDeptSaveRows(
      enriched.filter((r) => r.mod === 'A' || r.mod === 'U' || r.mod === 'D')
    ).map((r) => toOrgUnitSaveRow(r));

    // 수정/삭제는 DB org_unit_id 필수
    const bad = dirty.find(
      (r) => (r.mod === 'U' || r.mod === 'D') && !isDbOrgUnitId(r.orgUnitId)
    );
    if (bad) {
      throw new Error(
        `부서 ${bad.deptCode || ''}: DB id 가 없어 수정/삭제할 수 없습니다. 조회 후 다시 시도하세요.`
      );
    }

    await commonSaveList(
      ['HrOrgUnit.insertOrgUnit', 'HrOrgUnit.updateOrgUnit', 'HrOrgUnit.deleteOrgUnit'],
      dirty,
      { tenantCode: tc }
    );
    return loadDepts(tenantCode);
  }

  localStorage.setItem(
    deptStorageKey(tenantCode),
    JSON.stringify({
      tenantCode,
      rows: enriched.map(({ budgetRoleNm, orgLevelNm, acctUnitNm, ...rest }) => rest),
      savedAt: new Date().toISOString(),
    })
  );
  cacheSet(deptCache, tc, enriched);
  return enriched;
}

export function snapshotDept(row) {
  if (!row) return {};
  return {
    deptCode: row.deptCode || '',
    deptName: row.deptName || '',
    parentDeptCode: row.parentDeptCode || '',
    orgLevel: row.orgLevel || '',
    budgetRole: row.budgetRole || '',
    acctUnit: row.acctUnit || '',
    useYn: row.useYn || 'Y',
    sortOrder: row.sortOrder ?? '',
    pathCode: row.pathCode || '',
    remark: row.remark || '',
  };
}

export function loadDeptHistory(tenantCode, deptUid) {
  if (!deptUid) return [];
  try {
    const raw = localStorage.getItem(deptHistoryKey(tenantCode, deptUid));
    if (!raw) return [];
    const data = JSON.parse(raw);
    const rows = Array.isArray(data?.rows) ? data.rows : [];
    return rows
      .map((r) => ({
        ...r,
        changeTypeNm: changeTypeLabel(r.changeType),
        budgetRoleNm: budgetRoleLabel(r.snapshot?.budgetRole),
        acctUnitNm: acctUnitLabel(r.snapshot?.acctUnit, tenantCode),
        orgLevelNm: orgLevelLabel(r.snapshot?.orgLevel),
        changeAtDisp: formatDateTime(r.changeAt),
      }))
      .sort((a, b) => String(b.changeAt || '').localeCompare(String(a.changeAt || '')));
  } catch {
    return [];
  }
}

export function saveDeptHistory(tenantCode, deptUid, rows) {
  if (!deptUid) return;
  localStorage.setItem(
    deptHistoryKey(tenantCode, deptUid),
    JSON.stringify({
      tenantCode,
      deptUid,
      rows: rows || [],
      savedAt: new Date().toISOString(),
    })
  );
}

export function appendDeptHistory(tenantCode, deptUid, entry) {
  if (!deptUid || !entry) return [];
  const prev = (() => {
    try {
      const raw = localStorage.getItem(deptHistoryKey(tenantCode, deptUid));
      if (!raw) return [];
      const data = JSON.parse(raw);
      return Array.isArray(data?.rows) ? data.rows : [];
    } catch {
      return [];
    }
  })();
  const next = [
    {
      histId: createDeptUid(),
      changeType: entry.changeType || DEPT_CHANGE_TYPE.UPDATE,
      changeAt: entry.changeAt || new Date().toISOString(),
      changeBy: entry.changeBy || '',
      memo: entry.memo || '',
      snapshot: entry.snapshot || {},
    },
    ...prev,
  ];
  saveDeptHistory(tenantCode, deptUid, next);
  return loadDeptHistory(tenantCode, deptUid);
}

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * 저장 전후 비교 → 이력 적재
 * @returns {{ saved: object[], historyTouched: number }}
 */
export async function persistDeptsWithHistory(tenantCode, nextRows, { userId } = {}) {
  const tc = String(tenantCode || '').trim();
  const prevList = isBdApiEnabled()
    ? cacheGet(deptCache, tc) || []
    : loadRaw(tenantCode) || [];
  const prevByUid = new Map(prevList.map((r) => [r.deptUid, r]));
  const enriched = enrichDeptRows(nextRows || [], tenantCode);
  const byUid = new Map(enriched.map((r) => [r.deptUid, r]));
  let historyTouched = 0;
  const now = new Date().toISOString();
  const by = userId || '';

  enriched.forEach((row) => {
    if (!row.deptUid) return;
    const prev = prevByUid.get(row.deptUid);
    if (!prev) {
      appendDeptHistory(tenantCode, row.deptUid, {
        changeType: DEPT_CHANGE_TYPE.CREATE,
        changeAt: now,
        changeBy: by,
        memo: '부서 신규 등록',
        snapshot: snapshotDept(row),
      });
      historyTouched += 1;
      return;
    }
    if (row.mod === 'D' || (row.useYn === 'N' && prev.useYn !== 'N')) {
      // 폐지/삭제 표시는 아래에서 처리
    }
    const parentChanged = String(prev.parentDeptCode || '') !== String(row.parentDeptCode || '');
    const roleChanged = String(prev.budgetRole || '') !== String(row.budgetRole || '');
    const closed = prev.useYn === 'Y' && row.useYn === 'N';
    const otherChanged =
      String(prev.deptCode) !== String(row.deptCode) ||
      String(prev.deptName) !== String(row.deptName) ||
      String(prev.orgLevel) !== String(row.orgLevel) ||
      String(prev.acctUnit || '') !== String(row.acctUnit || '') ||
      String(prev.remark || '') !== String(row.remark || '') ||
      Number(prev.sortOrder) !== Number(row.sortOrder);

    if (closed) {
      appendDeptHistory(tenantCode, row.deptUid, {
        changeType: DEPT_CHANGE_TYPE.CLOSE,
        changeAt: now,
        changeBy: by,
        memo: '사용여부 N(폐지)',
        snapshot: snapshotDept(row),
      });
      historyTouched += 1;
    } else if (parentChanged) {
      appendDeptHistory(tenantCode, row.deptUid, {
        changeType: DEPT_CHANGE_TYPE.MOVE,
        changeAt: now,
        changeBy: by,
        memo: `상위 ${prev.parentDeptCode || '(없음)'} → ${row.parentDeptCode || '(없음)'}`,
        snapshot: snapshotDept(row),
      });
      historyTouched += 1;
    } else if (roleChanged) {
      appendDeptHistory(tenantCode, row.deptUid, {
        changeType: DEPT_CHANGE_TYPE.ROLE,
        changeAt: now,
        changeBy: by,
        memo: `역할 ${budgetRoleLabel(prev.budgetRole)} → ${budgetRoleLabel(row.budgetRole)}`,
        snapshot: snapshotDept(row),
      });
      historyTouched += 1;
    } else if (otherChanged && (row.mod === 'U' || row.mod === 'A')) {
      appendDeptHistory(tenantCode, row.deptUid, {
        changeType: DEPT_CHANGE_TYPE.UPDATE,
        changeAt: now,
        changeBy: by,
        memo: '속성 수정',
        snapshot: snapshotDept(row),
      });
      historyTouched += 1;
    }
  });

  prevList.forEach((prev) => {
    if (!byUid.has(prev.deptUid)) {
      appendDeptHistory(tenantCode, prev.deptUid, {
        changeType: DEPT_CHANGE_TYPE.CLOSE,
        changeAt: now,
        changeBy: by,
        memo: '부서 삭제',
        snapshot: snapshotDept(prev),
      });
      historyTouched += 1;
    }
  });

  const saved = await saveDepts(
    tenantCode,
    enriched.map((r) => ({ ...r, mod: r.mod === 'D' ? 'D' : r.mod || '' }))
  );
  return { saved, historyTouched };
}

/** 조회 필터 */
export function filterDepts(rows, { deptCode, deptName, budgetRole, useYn, orgLevel } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  return list.filter((r) => {
    if (budgetRole && r.budgetRole !== budgetRole) return false;
    if (useYn && r.useYn !== useYn) return false;
    if (orgLevel && r.orgLevel !== orgLevel) return false;
    if (deptCode && !String(r.deptCode || '').includes(String(deptCode).trim())) return false;
    if (deptName && !String(r.deptName || '').includes(String(deptName).trim())) return false;
    return true;
  });
}

export function nextSortOrderBy10(rows) {
  let max = 0;
  for (const r of rows || []) {
    const n = Number(r?.sortOrder);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return Math.ceil((max + 1) / 10) * 10;
}

export function validateDepts(rows) {
  const list = rows || [];
  const codes = new Set();
  for (const r of list) {
    if (r.mod === 'D') continue;
    const code = String(r.deptCode || '').trim();
    if (!code) return { ok: false, message: '부서코드는 필수입니다.' };
    if (!String(r.deptName || '').trim()) return { ok: false, message: `부서코드 ${code}: 부서명은 필수입니다.` };
    if (!r.budgetRole) return { ok: false, message: `부서코드 ${code}: 예산역할을 선택하세요.` };
    if (!r.orgLevel) return { ok: false, message: `부서코드 ${code}: 조직레벨을 선택하세요.` };
    if (codes.has(code)) return { ok: false, message: `부서코드 중복: ${code}` };
    codes.add(code);
  }
  for (const r of list) {
    if (r.mod === 'D') continue;
    const p = String(r.parentDeptCode || '').trim();
    if (!p) continue;
    if (p === String(r.deptCode)) return { ok: false, message: `${r.deptCode}: 상위부서로 자기 자신을 지정할 수 없습니다.` };
    if (!codes.has(p)) return { ok: false, message: `${r.deptCode}: 상위부서 ${p} 가 목록에 없습니다.` };
  }
  return { ok: true };
}
